import React, { useMemo } from "react";
import { observer } from "mobx-react-lite";

import { useRootStore } from "../../../../store/contexts";
import { useDataGridHandlers } from "../../../../common/hooks";
import {
  DataGrid,
  DataGridColumn,
  DataGridRowAction,
} from "../../../../common/controls/lists/DataGrid";
import { Publisher } from "../../../../api/graphql";
import { PublisherListStore } from "../../store";
import { countryDisplayIdByDemonym } from "../../../country/store/CountryCommonStore";
import { countryDisplayId } from "../../../country/store/CountryCommonStore";
import { provinceStateDisplayId } from "../../../provinceState/store/ProvinceStateCommonStore";
import { publisherRepresentativeDisplayId } from "../../../publisherRepresentative/store/PublisherRepresentativeCommonStore";
import { publisherCollectingSocietyDisplayId } from "../../../publisherCollectingSociety/store/PublisherCollectingSocietyCommonStore";
import { TenantSetupGuide } from "../../../tenantProfile/components/common/TenantSetupGuide";

import {
  VisibilityIcon,
  EditIcon,
  DeleteForeverIcon,
  ContentCopyIcon,
} from "../../../../icons";

export type GridExpressProps = { listStore?: PublisherListStore };

export const PublisherListForm: React.FC<GridExpressProps> = observer(
  ({ listStore }) => {
    const rootStore = useRootStore();
    const { langStore } = rootStore;

    if (!listStore) {
      return null;
    }

    const store = listStore;

    // Handlers de DataGrid (extraídos a hook común)
    const {
      handleColumnVisibilityChange,
      handleColumnOrderChange,
      handleColumnSizingChange,
      handleFiltersChange,
      handleSortingChange,
      handlePaginationChange,
    } = useDataGridHandlers(store, rootStore);

    // Row actions
    const rowActions: DataGridRowAction<Publisher>[] = useMemo(
      () => [
        {
          label: langStore.viewCommand,
          onClick: (row: Publisher, event?: React.MouseEvent) =>
            store.viewRow(row.id, event, "menu"),
          icon: <VisibilityIcon fontSize="small" />,
        },
        {
          label: langStore.editCommand,
          onClick: (row: Publisher, event?: React.MouseEvent) =>
            store.editRow(row.id, event, "menu"),
          icon: <EditIcon fontSize="small" />,
          isEnabled: () => store.isActionEnabled("update"),
        },
        {
          label: langStore.deleteCommand,
          onClick: (row: Publisher) => store.deleteRow(row.id),
          icon: <DeleteForeverIcon fontSize="small" />,
          isEnabled: () => store.isActionEnabled("delete"),
        },
        {
          label: langStore.duplicateCommand,
          onClick: (row: Publisher, event?: React.MouseEvent) =>
            store.duplicateRow(row.id, event, "menu"),
          icon: <ContentCopyIcon fontSize="small" />,
          isEnabled: () => store.isActionEnabled("create"),
        },
      ],
      [langStore, store]
    );

    // Columnas del store con accessorFn para referencias
    const columns = useMemo(() => {
      const storeColumns = store.grid
        .dataGridColumns as unknown as DataGridColumn<Publisher>[];
      return storeColumns.map((col) => {
        if (col.accessorKey === "personType") {
          return {
            ...col,
            id: "personType",
            accessorFn: (row: Publisher) => {
              const v = (row as any).personType;
              if (v === null || v === undefined || v === "") return "-";
              const opts = langStore.PersonTypeOptions;
              const toLabel = (x: any) => {
                const o = opts.find((opt: any) => opt.id === x);
                return o ? o.displayId : x;
              };
              return Array.isArray(v)
                ? v.length
                  ? v.map(toLabel).join("\n")
                  : "-"
                : toLabel(v);
            },
          };
        }
        if (col.accessorKey === "maritalStatus") {
          return {
            ...col,
            id: "maritalStatus",
            accessorFn: (row: Publisher) => {
              const v = (row as any).maritalStatus;
              if (v === null || v === undefined || v === "") return "-";
              const opts = langStore.MaritalStatusOptions;
              const toLabel = (x: any) => {
                const o = opts.find((opt: any) => opt.id === x);
                return o ? o.displayId : x;
              };
              return Array.isArray(v)
                ? v.length
                  ? v.map(toLabel).join("\n")
                  : "-"
                : toLabel(v);
            },
          };
        }
        if (col.accessorKey === "nationality") {
          return {
            ...col,
            id: "nationality",
            accessorFn: (row: Publisher) =>
              row.nationality
                ? countryDisplayIdByDemonym(row.nationality, langStore)
                : "-",
          };
        }
        if (col.accessorKey === "country") {
          return {
            ...col,
            id: "country",
            accessorFn: (row: Publisher) =>
              row.country ? countryDisplayId(row.country, langStore) : "-",
          };
        }
        if (col.accessorKey === "province") {
          return {
            ...col,
            id: "province",
            accessorFn: (row: Publisher) =>
              row.province
                ? provinceStateDisplayId(row.province, langStore)
                : "-",
          };
        }
        if (col.accessorKey === "representatives") {
          return {
            ...col,
            id: "representatives",
            accessorFn: (row: Publisher) =>
              row.representatives?.length
                ? row.representatives
                    .map((x: any) =>
                      publisherRepresentativeDisplayId(x, langStore)
                    )
                    .join("\n")
                : "-",
          };
        }
        if (col.accessorKey === "managementSocieties") {
          return {
            ...col,
            id: "managementSocieties",
            accessorFn: (row: Publisher) =>
              row.managementSocieties?.length
                ? row.managementSocieties
                    .map((x: any) =>
                      publisherCollectingSocietyDisplayId(x, langStore)
                    )
                    .join("\n")
                : "-",
          };
        }
        if (col.accessorKey === "createdAt_") {
          return {
            ...col,
            id: "createdAt_",
            accessorFn: (row: Publisher) =>
              row.createdAt_
                ? new Date(row.createdAt_).toLocaleString("es-ES", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                : "-",
          };
        }
        if (col.accessorKey === "lastUpdatedAt_") {
          return {
            ...col,
            id: "lastUpdatedAt_",
            accessorFn: (row: Publisher) =>
              row.lastUpdatedAt_
                ? new Date(row.lastUpdatedAt_).toLocaleString("es-ES", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                : "-",
          };
        }
        return col;
      });
    }, [store.grid.dataGridColumns, langStore]);

    const rows = store.publisherCurrentPage || [];

    return (
      <div id={"ListOfPublisher"}>
        <TenantSetupGuide />
        <div id={"StartOfListingOfPublisher"} />
        <DataGrid
          columns={columns}
          data={rows}
          isLoading={store.status === "fetching"}
          enableFiltering
          enableShowHideFilters
          onFiltersChange={handleFiltersChange}
          initialFilters={store.grid.filtering}
          sorting={store.grid.sorting.map((s) => ({
            id: s.columnName,
            desc: s.direction === "desc",
          }))}
          onSortingChange={handleSortingChange}
          pagination={{
            pageIndex: store.currentPage - 1,
            pageSize: store.pageSize,
          }}
          onPaginationChange={handlePaginationChange}
          rowCount={store.totalRows + (store.hasMorePages ? 1 : 0)}
          manualPagination
          enableColumnOrdering
          enableColumnResizing
          enableHiding
          columnVisibility={store.grid.mrtColumnVisibility}
          onColumnVisibilityChange={handleColumnVisibilityChange}
          columnOrder={store.grid.columnOrder}
          onColumnOrderChange={handleColumnOrderChange}
          columnSizing={store.grid.mrtColumnSizing}
          onColumnSizingChange={handleColumnSizingChange}
          showColumnFilters={store.grid.showColumnFilters}
          onShowColumnFiltersChange={(value) =>
            store.grid.setShowColumnFilters(value)
          }
          isFullScreen={store.grid.isFullScreen}
          onIsFullScreenChange={(value) => store.grid.setIsFullScreen(value)}
          getRowId={(row) => row.id}
          language={langStore.language as "es" | "en" | "pt"}
          onRowClick={(row, event) => store.handleRowClick(row, event)}
          selectedRowId={store.selectedRowId}
          rowActions={rowActions}
          muiTableBodyRowProps={() => ({
            sx: { cursor: "pointer" },
          })}
        />
        <div id={"EndOfListing"} />
      </div>
    );
  }
);
